import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Anchor,
    DropdownPopper,
    TextButton,
    Tooltip,
    TriggerText,
} from '@zillow/constellation';

// constants
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

import {
    GA_EVENTS,
    GA_PREFIX,
} from 'app/shared/constants/AnalyticsListingModuleConstants';

import { LISTING_SUMMARY_DISPLAY_STATUS } from 'app/shared/constants/ListingSummaryConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import stringHelper from 'app/shared/utils/stringHelper';

import {
    StyledData,
    StyledDetailsComponent,
    StyledHeader,
    StyledItemContainer,
    StyledTooltipText,
    StyledWeeklyFee,
} from './StyledComponents';

const Details = (props) => {
    const dispatch = useDispatch();

    const {
        isAutoBillEnabled,
        lastListed,
        rentZestimate,
        status,
        renewalPrice,
    } = props;

    let { listedFor, renews } = props;

    // display rent zestimate when inactive
    const renderAsCurrency = status === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE && rentZestimate !== '-';
    const rentZestimateCurrency = renderAsCurrency
        ? numberUtils.asCurrency(rentZestimate)
        : '-';

    const rentZesimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/';

    const expireOrRenewTitle = isAutoBillEnabled ? 'Renews' : 'Expires';

    // check if weekly fee is the default value
    let weeklyFeeCurrency = renewalPrice !== '-'
        ? numberUtils.asCurrency(renewalPrice, '$0,0.00')
        : renewalPrice;

    // should not display if listing is declined
    if (status === LISTING_SUMMARY_DISPLAY_STATUS.DECLINED) {
        weeklyFeeCurrency = '-';
        listedFor = '-';
        renews = '-';
    }

    const handleLearnMoreZestimateClick = () => {
        const { ZESTIMATE_LEARN_MORE_CLICK } = GA_EVENTS;
        const { LEARN_MORE_ZESTIMATE } = GA_PREFIX;

        dispatch(analyticsUtils.track.GAEventV2({
            ...ZESTIMATE_LEARN_MORE_CLICK,
            action: `${LEARN_MORE_ZESTIMATE}${DELIMITER}${stringHelper.startCaseRemoveSpace(status)}`,
        }));
    };

    const renderLink = () => (
        <Anchor
            target="_blank"
            href={rentZesimateLink}
        >
            <TextButton
                fontType="body"
                onClick={handleLearnMoreZestimateClick}
            >
                Learn more
            </TextButton>
        </Anchor>
    );

    const renderTooltip = (
        <Tooltip
            body={(
                <StyledTooltipText fontType="body">
                    The Rent Zestimate is Zillow’s best estimate of a
                    home’s value as a rental property.
                    {' '}
                    {renderLink()}
                    {' about the Rent Zestimate.'}
                </StyledTooltipText>
            )}
            shouldCloseOnOutsideClick={true}
        />
    );

    const renderDetails = () => (
        <>
            <StyledItemContainer>
                <StyledHeader fontType="bodySmall" as="div">Listed for</StyledHeader>
                <StyledData fontType="bodyHeading" as="div">{listedFor}</StyledData>
            </StyledItemContainer>
            <StyledItemContainer>
                <StyledHeader fontType="bodySmall" as="div">{expireOrRenewTitle}</StyledHeader>
                <StyledData fontType="bodyHeading" as="div">{renews}</StyledData>
            </StyledItemContainer>
            <StyledItemContainer>
                <StyledHeader fontType="bodySmall" as="div">Weekly fee</StyledHeader>
                <StyledData fontType="bodyHeading" as="div">{weeklyFeeCurrency}</StyledData>
            </StyledItemContainer>

            <StyledWeeklyFee fontType="finePrint" as="div">
                Weekly fee:
                {' '}
                {weeklyFeeCurrency}
            </StyledWeeklyFee>
        </>
    );

    const renderInactiveDetails = () => (
        <>
            <StyledItemContainer>
                <StyledHeader fontType="bodySmall" as="div">Last listed</StyledHeader>
                <StyledData fontType="bodyHeading" as="div">{lastListed}</StyledData>
            </StyledItemContainer>
            <StyledItemContainer>
                <StyledHeader fontType="bodySmall" as="div">Rent Zestimate ®</StyledHeader>
                <StyledData fontType="bodyHeading" as="div">
                    <DropdownPopper
                        triggered={renderTooltip}
                        shouldCloseOnOutsideClick={true}
                    >
                        <TriggerText
                            closeWith="mouseLeave"
                        >
                            {rentZestimateCurrency}
                        </TriggerText>
                    </DropdownPopper>
                </StyledData>
            </StyledItemContainer>
        </>
    );

    return (
        <StyledDetailsComponent className="Details">
            {status === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE
                ? renderInactiveDetails()
                : renderDetails()
            }
        </StyledDetailsComponent>
    );
};

Details.propTypes = {
    isAutoBillEnabled: PropTypes.bool.isRequired,
    lastListed: PropTypes.string,
    listedFor: PropTypes.string,
    renewalPrice: PropTypes.string,
    renews: PropTypes.string,
    rentZestimate: PropTypes.string,
    status: PropTypes.string.isRequired,
};

Details.defaultProps = {
    lastListed: '-',
    listedFor: '-',
    renewalPrice: '-',
    renews: '-',
    rentZestimate: '-',
};

export default Details;
